import {distinctUntilChanged} from 'rxjs/operators';
import {Component, HostListener, OnInit} from '@angular/core';
import {Keepalive} from '@ng-idle/keepalive';
import {DEFAULT_INTERRUPTSOURCES, Idle} from '@ng-idle/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {HttpClient} from '@angular/common/http';
import {environment} from '../environments/environment';
import {UcrSecurityService} from './core/ucr-security/ucr-security.service';
import {SwitchUserComponent} from './core/switch-user/switch-user.component';
import {Roles} from './core/ucr-security/roles';
import {UserInfo} from './core/ucr-security/user-info';
import {TextService} from './db-text/text.service';
import {AlertService} from './shared/services/alert.service';
import {Subscription} from 'rxjs';
import {HealthService} from "./shared/services/health.service";
import {Health} from "./shared/interface/health.interfaces";

export let browserRefresh = false;

@Component({
    selector: 'app-ucr-app',
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
    subscription: Subscription;

    envName = environment.envName;
    keepalivePing = 60; // Ping session every 60 seconds
    roles = Roles;
    sessionLength: number = 60 * 30; // 30 minutes
    sessionWarnBuffer: number = 60 * 5; // Warn 5 minutes before session is going to expire because of inactivity

    static onLoggedInStatusChanged(isLoggedIn: boolean, self: AppComponent): void {
        if (isLoggedIn) {
            self.idle.watch();
        } else {
            self.idle.stop();
        }
    }

    static onSessionIdleStart(self: AppComponent): void {
        self.alert.confirmNoCancel({
            title: 'Are you still there?',
            text: 'Your session is about to expire.',
            okButtonText: "I'm still here",
            callBack: {
                this: this,
                function: (result: boolean) => {
                    if (result) {
                        self.keepalive.ping();
                        self.keepalive.start();
                    }
                }
            }
        });
    }

    static onSessionPing(self: AppComponent): void {
        self.http.get('/application/heartbeat').subscribe(() => {
            // ignored
        });
    }

    static onSessionTimeout(self: AppComponent): void {
        self.alert.close();
        self.ucrSecurity.logout();
    }

    constructor(
        public idle: Idle,
        public keepalive: Keepalive,
        public alert: AlertService,
        public http: HttpClient,
        public router: Router,
        public ucrSecurity: UcrSecurityService,
        public dialog: MatDialog,
        private textService: TextService,
        private healthService: HealthService
    ) {
        // sets an idle timeout. After this amount of time the user will be warned.
        idle.setIdle(this.sessionLength - this.sessionWarnBuffer);
        // sets a session timeout period. After this amount of inactivity (after the user is considered idle),
        // the user will be considered timed out.
        idle.setTimeout(this.sessionWarnBuffer);
        // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        idle.onTimeout.subscribe(() => AppComponent.onSessionTimeout(this));
        idle.onIdleStart.subscribe(() => AppComponent.onSessionIdleStart(this));

        // sets the ping interval
        keepalive.interval(this.keepalivePing);
        keepalive.onPing.subscribe(() => AppComponent.onSessionPing(this));

        ucrSecurity.onLoggedInStatusChanged.subscribe((isLoggedIn) =>
            AppComponent.onLoggedInStatusChanged(isLoggedIn, this)
        );

        this.subscription = router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                browserRefresh = !router.navigated;
            }
        });

        if (environment.enableGoogleAnalytics) {
            router.events
                .pipe(
                    distinctUntilChanged((previous: any, current: any) => {
                        if (current instanceof NavigationEnd) {
                            return previous.url === current.url;
                        }

                        return true;
                    })
                )
                .subscribe((routeEvent: any) => {
                    ga('set', 'page', routeEvent.urlAfterRedirects);
                    ga('send', 'pageview');
                });
        }

        this.healthService.getHealth().subscribe((item: Health) => {
            this.healthService.setCurrent(item);
        });
    }

    @HostListener('window:keyup', ['$event'])
    keyboardInput(event: KeyboardEvent): void {
        let triggerDialog = false;
        try {
            if (event.altKey && event.key === 'u') {
                triggerDialog = true;
            }
        } catch (ignored) {
            // ignored
        }

        if (triggerDialog) {
            this.ucrSecurity.getUserInfo().subscribe((ui: UserInfo) => {
                if (ui.ifAllGranted([this.roles.switchUser])) {
                    this.dialog.open(SwitchUserComponent);
                }
            });
        }
    }

    ngOnInit(): void {
        this.router.events.subscribe((evt) => {
            if (!(evt instanceof NavigationEnd)) {
                return;
            }
            window.scrollTo(0, 0);
        });
    }

    textComponentEvent($event: boolean) {
        this.textService.enableDisableEdit($event);
    }
}
